import React from "react"
import { motion } from "framer-motion"
import { useNavigate } from "react-router-dom"
import { Camera } from "lucide-react"

import { supabase } from "../lib/supabaseClient"
import { NiyyahLogo } from "../components/intake/dashboard/NiyyahLogo"

export function SettingsPage() {
  const navigate = useNavigate()

  const [userEmail, setUserEmail] = React.useState("")
  const [userPlan, setUserPlan] = React.useState<string>("")
  const [loading, setLoading] = React.useState(true)

  // Profile picture state (persisted locally)
  const [profilePicture, setProfilePicture] = React.useState<string | null>(
    localStorage.getItem("niyyah_profile_picture"),
  )

  React.useEffect(() => {
    let cancelled = false

    const fetchPlanForEmail = async (email: string) => {
      if (!email) {
        setUserPlan("")
        return
      }

      try {
        const url = import.meta.env.VITE_MEMBER_STATUS_URL as string | undefined
        const anon = (import.meta.env.VITE_SUPABASE_ANON_KEY as string) || ""

        if (!url) {
          setUserPlan("")
          return
        }

        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(anon ? { Authorization: `Bearer ${anon}`, apikey: anon } : {}),
          },
          body: JSON.stringify({ email }),
        })

        const json = await res.json().catch(() => ({}))

        // plan: 1 basic, 2 active, null
        const planNum = json?.plan ?? null
        const planLabel = planNum === 2 ? "Active Matches" : planNum === 1 ? "Basic" : ""

        setUserPlan(planLabel)
      } catch {
        // ignore
        setUserPlan("")
      }
    }

    const syncUser = async () => {
      setLoading(true)

      const { data } = await supabase.auth.getUser()
      const email = (data?.user?.email || "").trim()

      if (cancelled) return

      setUserEmail(email)
      await fetchPlanForEmail(email)

      if (!cancelled) setLoading(false)
    }

    // initial
    syncUser()

    // keep in sync if auth state changes
    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      syncUser()
    })

    return () => {
      cancelled = true
      sub.subscription.unsubscribe()
    }
  }, [])

  const userName = userEmail ? userEmail.split("@")[0] : ""
  const userInitial = (userName?.charAt(0) || "U").toUpperCase()

  const handleProfilePictureUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Basic guard (avoid huge localStorage payloads)
    const maxBytes = 2_000_000 // 2MB
    if (file.size > maxBytes) {
      // eslint-disable-next-line no-alert
      alert("Please upload an image smaller than 2MB.")
      event.target.value = ""
      return
    }

    const reader = new FileReader()
    reader.onloadend = () => {
      const result = reader.result as string
      setProfilePicture(result)
      localStorage.setItem("niyyah_profile_picture", result)
    }
    reader.readAsDataURL(file)
  }

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut()
    } catch {}

    // optional cleanup (fine to keep)
    localStorage.removeItem("niyyah_email")
    localStorage.removeItem("niyyah_intake_complete")
    localStorage.removeItem("niyyah_plan")
    localStorage.removeItem("niyyah_resume")
    localStorage.removeItem("niyyah_profile_picture")

    navigate("/login", { replace: true })
  }

  const handleManageBilling = () => {
    window.open(import.meta.env.VITE_BILLING_PORTAL_URL, "_blank")
  }

  const handleUpgrade = () => {
    navigate("/?step=payment&plan=active")
  }

  const handleRequestDeletion = () => {
    window.location.href = "mailto:support@yourdomain.com?subject=Account%20Deletion%20Request"
  }

  return (
    <div className="min-h-screen bg-white w-full">
      {/* Header */}
      <header className="w-full px-6 py-12 md:px-12 md:py-16 max-w-6xl mx-auto">
        <div className="flex items-center justify-between">
          <NiyyahLogo />
          <button
            onClick={() => navigate("/dashboard" + window.location.search)}
            className="text-sm text-niyyah-muted hover:text-niyyah-purple transition-colors font-light"
          >
            Back to Dashboard
          </button>
        </div>
      </header>

      <main className="w-full pb-32">
        {/* Hero Section */}
        <section className="px-6 md:px-12 max-w-2xl mx-auto mb-32 md:mb-40 text-center">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.5 }}>
            <h1 className="text-5xl md:text-6xl font-light text-niyyah-text mb-6 tracking-tight">
              Settings
            </h1>
            <p className="text-lg md:text-xl text-niyyah-muted font-light leading-relaxed">
              Manage your account and preferences
            </p>
          </motion.div>
        </section>

        {/* Settings Sections */}
        <section className="px-6 md:px-12 max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="space-y-12"
          >
            {/* Profile Picture Section */}
            <div>
              <h2 className="text-xs font-medium uppercase tracking-widest gradient-text-header mb-6">
                Profile
              </h2>
              <div className="p-8 rounded-3xl bg-white border border-gray-100 text-center">
                <div className="flex flex-col items-center space-y-6">
                  <div className="relative group">
                    {profilePicture ? (
                      <img
                        src={profilePicture}
                        alt="Profile"
                        className="w-24 h-24 rounded-full object-cover border-2 border-niyyah-light/20"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-niyyah-pale flex items-center justify-center text-3xl text-niyyah-purple font-light border-2 border-niyyah-light/20">
                        {userInitial}
                      </div>
                    )}

                    <label
                      htmlFor="profile-picture-upload"
                      className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center cursor-pointer"
                      aria-label="Upload profile picture"
                      title="Upload profile picture"
                    >
                      <Camera className="w-6 h-6 text-white" />
                    </label>

                    <input
                      id="profile-picture-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePictureUpload}
                      className="hidden"
                    />
                  </div>

                  <div>
                    <p className="text-xs text-niyyah-muted font-light mb-2">
                      {profilePicture ? "Change your profile picture" : "Add a profile picture"}
                    </p>
                    <label
                      htmlFor="profile-picture-upload"
                      className="text-xs text-niyyah-purple hover:text-niyyah-text transition-colors font-light cursor-pointer underline"
                    >
                      Upload photo
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Account Section */}
            <div>
              <h2 className="text-xs font-medium uppercase tracking-widest gradient-text-header mb-6">
                Account
              </h2>
              <div className="p-8 rounded-3xl bg-white border border-gray-100">
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-niyyah-muted font-light mb-2 uppercase tracking-wider">
                      Email
                    </label>
                    <p className="text-sm text-niyyah-text font-light">
                      {loading ? "Loading..." : userEmail || "—"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Membership Section */}
            <div>
              <h2 className="text-xs font-medium uppercase tracking-widest gradient-text-header mb-6">
                Membership
              </h2>
              <div className="p-8 rounded-3xl bg-white border border-gray-100 text-center">
                <div className="space-y-6">
                  <div>
                    <label className="block text-xs text-niyyah-muted font-light mb-2 uppercase tracking-wider">
                      Current Plan
                    </label>
                    <p className="text-sm text-niyyah-text font-light mb-6">
                      {loading ? "Loading..." : userPlan || "—"}
                    </p>
                  </div>

                  {userPlan ? (
                    <button onClick={handleManageBilling} className="settings-button-circle mx-auto">
                      <span className="text-sm font-light leading-tight">
                        Manage
                        <br />
                        billing
                      </span>
                    </button>
                  ) : (
                    <button onClick={handleUpgrade} className="settings-button-circle mx-auto">
                      <span className="text-sm font-light leading-tight">Upgrade</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Privacy Section */}
            <div>
              <h2 className="text-xs font-medium uppercase tracking-widest gradient-text-header mb-6">
                Privacy
              </h2>
              <div className="p-8 rounded-3xl bg-white border border-gray-100 text-center">
                <div className="space-y-6">
                  <div>
                    <p className="text-sm text-niyyah-muted font-light mb-6">
                      Request permanent deletion of your account and all associated data.
                    </p>
                  </div>
                  <button
                    onClick={handleRequestDeletion}
                    className="settings-button-circle-secondary mx-auto"
                  >
                    <span className="text-xs font-light leading-tight px-3">
                      Request account deletion
                    </span>
                  </button>
                </div>
              </div>
            </div>

            {/* Log Out Section */}
            <div className="pt-8 border-t border-gray-100">
              <button
                onClick={handleLogout}
                className="text-sm text-niyyah-muted hover:text-niyyah-text transition-colors font-light"
              >
                Log out
              </button>
            </div>
          </motion.div>
        </section>

        {/* Footer Links */}
        <section className="px-6 md:px-12 max-w-2xl mx-auto mt-32">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="flex items-center justify-center gap-6 text-xs text-niyyah-muted font-light"
          >
            <a href="/terms" className="hover:text-niyyah-purple transition-colors">
              Terms of Service
            </a>
            <span>•</span>
            <a href="/privacy" className="hover:text-niyyah-purple transition-colors">
              Privacy Policy
            </a>
          </motion.div>
        </section>
      </main>

      <style>{`
        .gradient-text-header {
          background-image:
            url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E"),
            linear-gradient(
              135deg,
              #7a6ba8 0%,
              #8b7db8 12%,
              #9b8fc4 25%,
              #8c9ed8 38%,
              #7ba5d9 50%,
              #6bc4e0 62%,
              #b89bc8 75%,
              #9b8fc4 88%,
              #7a6ba8 100%
            );
          background-size: 200px 200px, 400% 400%;
          background-blend-mode: overlay, normal;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: logoGradientFluctuate 12s ease-in-out infinite;
        }

        .settings-button-circle {
          position: relative;
          width: 100px;
          height: 100px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          transition: transform 0.3s ease;
          overflow: hidden;
          background: #9b8fc4;
        }

        .settings-button-circle::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
          background-size: 200px 200px;
          opacity: 0.5;
          mix-blend-mode: overlay;
          pointer-events: none;
          z-index: 1;
        }

        .settings-button-circle span { position: relative; z-index: 2; }
        .settings-button-circle:hover { transform: scale(1.05); }

        .settings-button-circle-secondary {
          position: relative;
          width: 120px;
          height: 120px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6b7280;
          transition: transform 0.3s ease;
          overflow: hidden;
          background: #f3f4f6;
        }

        .settings-button-circle-secondary::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
          background-size: 200px 200px;
          opacity: 0.3;
          mix-blend-mode: overlay;
          pointer-events: none;
          z-index: 1;
        }

        .settings-button-circle-secondary span { position: relative; z-index: 2; }
        .settings-button-circle-secondary:hover { transform: scale(1.05); }

        @keyframes logoGradientFluctuate {
          0%, 100% { background-position: 0px 0px, 0% 50%; }
          25% { background-position: 0px 0px, 100% 50%; }
          50% { background-position: 0px 0px, 50% 100%; }
          75% { background-position: 0px 0px, 0% 100%; }
        }
      `}</style>
    </div>
  )
}